/**
 * Utility functions for handling markdown content
 */

/**
 * Strips basic markdown formatting from text
 * @param text - The text containing markdown
 * @returns Clean text without markdown formatting
 */
export const stripMarkdown = (text: string): string => {
  if (!text) return '';
  
  return text
    // Remove bold/strong formatting (**text** or __text__)
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    // Remove italic formatting (*text* or _text_)
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    // Remove headers (# ## ### etc.)
    .replace(/^#{1,6}\s+/gm, '')
    // Remove strikethrough (~~text~~)
    .replace(/~~(.*?)~~/g, '$1')
    // Remove inline code (`text`)
    .replace(/`([^`]+)`/g, '$1')
    // Remove code blocks (```text```)
    .replace(/```[\s\S]*?```/g, '')
    // Remove links [text](url) - keep only the text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove reference-style links [text][ref]
    .replace(/\[([^\]]+)\]\[[^\]]*\]/g, '$1')
    // Remove images ![alt](url)
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    // Remove horizontal rules (--- or ***)
    .replace(/^[-*]{3,}$/gm, '')
    // Remove blockquotes (> text)
    .replace(/^>\s+/gm, '')
    // Remove bullet points and numbered lists
    .replace(/^[\s]*[-*+]\s+/gm, '')
    .replace(/^[\s]*\d+\.\s+/gm, '')
    // Clean up extra whitespace
    .replace(/\n\s*\n/g, '\n\n')
    .trim();
};

/**
 * Formats text for display with proper line breaks and spacing
 * @param text - The text to format
 * @returns Formatted text with consistent spacing
 */
export const formatDisplayText = (text: string): string => {
  if (!text) return '';
  
  return stripMarkdown(text)
    // Ensure consistent line breaks
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Normalize multiple line breaks
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}; 